import { useTranslation } from 'react-i18next';

import { useAuth } from '@auth/contexts/AuthContext';
import { LoadingSpinner } from '@core/components/LoadingSpinner';
import { useIpAddress } from '@core/hooks/useIpAddress';

interface AppInitializerProps {
  children: React.ReactNode;
}

/**
 * A component that initializes the app by checking the authentication status.
 * It shows a loading spinner while auth is initializing and IP address is being fetched.
 * The IP address is optional but we wait for the request to complete to avoid state flashes.
 * Connection failure is handled by the ConnectionError component in the axios interceptors.
 *
 * @example
 * ```tsx
 * <AppInitializer>
 *   <Content />
 * </AppInitializer>
 * ```
 */
export default function AppInitializer({ children }: AppInitializerProps) {
  const { isLoading: authLoading } = useAuth();
  const { isLoading: ipLoading } = useIpAddress();
  const { t } = useTranslation();

  if (ipLoading || authLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner text={t('common.initializing')} />
      </div>
    );
  }

  return <>{children}</>;
}
